import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Helmet } from "react-helmet";
import { saveAs } from "file-saver";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import CreditsBanner from "../Components/CreditsNudge";
import { message } from "../Constants/message.js";
import TimestampGeneratorContent from "../Contents/YouTubeTimestampGeneratorContent.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeTranscriptWithTimestamps({ user, login, isLoggedIn }) {
  const theme = useTheme();
  const isMobile = useTheme().breakpoints.down("sm");

  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timestamps, setTimestamps] = useState([]);
  const [credits, setCredits] = useState(0);

  const handleUrlChange = (e) => setVideoUrl(e.target.value);

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleFetch = async () => {
    setError("");
    setSuccess("");
    setTimestamps([]);

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => login(), 1500);
      return;
    }
    if (credits.remaining < 1) {
      setError(
        "Insufficient credits to generate YouTube Timestamp. Please upgrade your plan."
      );
      return;
    }
    if (!videoUrl.includes("youtube.com") && !videoUrl.includes("youtu.be")) {
      setError(
        "Please enter a valid YouTube video URL. It should be like https://www.youtube.com/watch?v=VIDEO_ID"
      );
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube-timestamper-generator`,
        { videoUrl, generateTimestamps: true },
        { headers: { "Content-Type": "application/json" } }
      );

      setTimestamps(response.data.timestamps || []);
      setSuccess(response.data.note);

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to fetch timestamps. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const content = timestamps.join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `timestamps.txt`);
  };

  const handleCopyAll = () => {
    const allText = timestamps.join("\n");
    navigator.clipboard.writeText(allText);
  };

  return (
    <>
      <Helmet>
        <title>YouTube Timestamp Generator | YouTube Chapters</title>
        <meta
          name="description"
          content="Generate YouTube video timestamps and download or copy them easily. Fast, free, and accurate to add chapters to youtube video"
        />
      </Helmet>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3, px: isMobile ? 2 : 6 }}>
        <ContentBox>
          <TitleText variant={isMobile ? "h4" : "h2"} align="center">
            YouTube Timestamp Generator
          </TitleText>

          <SubtitleText
            variant={isMobile ? "subtitle1" : "h6"}
            align="center"
            sx={{ mb: 2 }}
          >
            Quickly Generate YouTube Video Chapters and Timestamps that You Can
            Copy or Download
          </SubtitleText>

          <TextField
            fullWidth
            label="Paste YouTube Video URL"
            variant="outlined"
            value={videoUrl}
            onChange={handleUrlChange}
            placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
            size="medium"
            sx={{ mb: 2 }}
            name="primary-textbox"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ✅ <b>1 Video Chapter Generation = 1 Credit</b>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ensure the video is public
          </Typography>
          <Typography />
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            gap={2}
            mb={3}
          >
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleFetch}
              startIcon={<DownloadIcon />}
            >
              Fetch Timestamps
            </StyledButton>
          </Box>

          {timestamps.length > 0 && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 2,
                background: "#f9f9f9",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Your Chapters</Typography>
                <Box>
                  <Tooltip title="Download TXT">
                    <IconButton onClick={handleDownload}>
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy All">
                    <IconButton onClick={handleCopyAll}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {timestamps.map((line, idx) => (
                <Typography
                  key={idx}
                  variant="body1"
                  sx={{ mb: 0.5, whiteSpace: "pre-wrap" }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube-timestamper-generator"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <TimestampGeneratorContent />
      </PageContainer>
    </>
  );
}

export default YouTubeTranscriptWithTimestamps;
